import React from 'react';
import { STORAGE_URL } from '../../services/api';

const CertificatePreview = ({ data = {}, photos = {}, language = 'marathi', stamp = null, logo = null, authoritySignature = null }) => {
  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    return {
      dd: String(d.getDate()).padStart(2, '0'),
      mm: String(d.getMonth() + 1).padStart(2, '0'),
      yyyy: String(d.getFullYear()),
      display: d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    };
  };

  const marriageDate = formatDate(data.marriage_date);
  const certificateDate = formatDate(data.certificate_date);

  // Resolve image URLs
  const resolveUrl = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) return value;
      return `${STORAGE_URL}/${value}`;
    }
    try { return URL.createObjectURL(value); } catch { return null; }
  };

  const husbandPhotoUrl = resolveUrl(photos?.husbandPhoto || data.husband_photo);
  const wifePhotoUrl = resolveUrl(photos?.wifePhoto || data.wife_photo);
  const husbandSigUrl = resolveUrl(photos?.husbandSignature || data.husband_signature);
  const wifeSigUrl = resolveUrl(photos?.wifeSignature || data.wife_signature);
  const stampUrl = resolveUrl(stamp);
  const authoritySignatureUrl = resolveUrl(authoritySignature);
  const qrUrl = data.qr_code ? resolveUrl(data.qr_code) : null;

  const officeName = data.office_name || '';
  const officeAddress = data.office_address || '';

  const showMarathi = language === 'marathi' || language === 'both';
  const showEnglish = language === 'english' || language === 'both';

  // Styles matching backend template
  const styles = {
    // Main container - A4 proportioned
    container: {
      fontFamily: '"Noto Sans Devanagari", Georgia, serif',
      aspectRatio: '210 / 297',
      width: '100%',
      maxWidth: 794,
      margin: '0 auto',
      background: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
    },
    // Outer border
    outerBorder: {
      position: 'absolute',
      inset: 0,
      border: '3px solid #b8860b',
      pointerEvents: 'none',
      boxSizing: 'border-box',
    },
    // Inner border
    innerBorder: {
      position: 'absolute',
      inset: '2mm',
      border: '1px solid #d4a843',
      pointerEvents: 'none',
      boxSizing: 'border-box',
    },
    // Content wrapper
    content: {
      position: 'relative',
      padding: '3mm 5mm',
      height: '100%',
      boxSizing: 'border-box',
    },
    // Top band
    topBand: {
      textAlign: 'center',
      padding: '0',
      marginBottom: '2px',
    },
    titleMarathi: {
      fontSize: '17px',
      fontWeight: 700,
      color: '#1a3a6b',
      lineHeight: 1.15,
      marginBottom: '1px',
    },
    titleEnglish: {
      fontSize: '17px',
      fontWeight: 700,
      color: '#1a3a6b',
      letterSpacing: '1px',
      marginTop: '3px',
      marginBottom: '4px',
    },
    subText: {
      fontSize: '11px',
      color: '#1a3a6b',
      marginTop: '1px',
    },
    regNo: {
      fontSize: '11px',
      color: '#1a3a6b',
      fontWeight: 'bold',
      marginTop: '1px',
    },
    divider: {
      width: '240px',
      margin: '4px auto',
      borderTop: '1px solid #d4a843',
    },
    goldDivider: {
      height: '1px',
      background: '#d4a843',
      margin: '2px 0',
    },
    certNumRow: {
      textAlign: 'right',
      fontSize: '10px',
      color: '#1a3a6b',
      padding: '3px 0',
      borderBottom: '0.5px solid #e0d5b5',
    },
    // Photo container
    photoContainer: {
      position: 'relative',
      width: '75px',
      height: '95px',
      margin: '0 auto',
    },
    photoFrame: {
      width: '75px',
      height: '95px',
      border: '1.5px solid #9c7a2e',
      background: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    stampOverlay: {
      position: 'absolute',
      width: '58px',
      height: '58px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
    },
    photoCaption: {
      fontSize: '10px',
      color: '#1a3a6b',
      marginTop: '2px',
      textAlign: 'center',
    },
    // Signature box
    sigBox: {
      width: '95px',
      height: '24px',
      borderBottom: '1.5px solid #9c7a2e',
      margin: '5px auto 2px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    sigCaption: {
      fontSize: '9px',
      color: '#1a3a6b',
      textAlign: 'center',
    },
    // Body text
    bodyBlock: {
      fontSize: '10px',
      lineHeight: 1.25,
      padding: '5px 7px',
      color: '#1a3a6b',
      border: '0.5px solid #d4a843',
      background: '#ffffff',
      marginBottom: '4px',
    },
    underline: {
      display: 'inline-block',
      minWidth: '45px',
      borderBottom: '1px solid #9c7a2e',
      fontWeight: 'bold',
      color: '#1a3a6b',
      paddingBottom: '1px',
      margin: '0 2px',
    },
    // Bottom section
    bottomTable: {
      width: '100%',
      marginTop: '5px',
    },
    detailLabel: {
      fontSize: '8px',
      color: '#1a3a6b',
    },
    detailValue: {
      borderBottom: '1px solid #9c7a2e',
      minWidth: '65px',
      fontWeight: 'bold',
      fontSize: '10px',
      display: 'inline-block',
      color: '#1a3a6b',
    },
    qrBox: {
      width: '60px',
      height: '60px',
      border: '1.5px solid #9c7a2e',
      background: '#ffffff',
      marginTop: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    stampBox: {
      width: '65px',
      height: '65px',
      margin: '0 auto 3px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    // Footer
    footerBand: {
      marginTop: '6px',
      borderTop: '1px solid #d4a843',
      borderBottom: '2px solid #d4a843',
      borderLeft: '2px solid #d4a843',
      borderRight: '2px solid #d4a843',
      padding: '3px 0',
      textAlign: 'center',
    },
    footerText: {
      fontSize: '8px',
      color: '#1a3a6b',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      fontWeight: 600,
    },
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet" />

      <div style={styles.container}>
        <div style={styles.outerBorder} />
        <div style={styles.innerBorder} />

        <div style={styles.content}>
          {/* Top Band - Marathi */}
          <div style={styles.topBand}>
            {showMarathi && (
              <div style={styles.titleMarathi}>विवाह प्रमाणपत्र</div>
            )}
            {showMarathi && (
              <div style={styles.subText}>हिंदू विवाह कायदा १९५५ (कलम ०७ पहा)</div>
            )}
            {showMarathi && (
              <div style={styles.regNo}>रजि. नं. {data.registration_no || '____________'}</div>
            )}

            {showMarathi && showEnglish && <div style={styles.divider} />}

            {/* English Header */}
            {showEnglish && (
              <>
                <div style={styles.titleEnglish}>
                  {showMarathi ? 'CERTIFICATE OF MARRIAGE' : 'Certificate of Marriage'}
                </div>
                <div style={styles.subText}>Hindu Marriage Act 1955 (See Section 07)</div>
                <div style={styles.regNo}>Reg. No. {data.registration_no || '____________'}</div>
              </>
            )}
          </div>

          <div style={styles.goldDivider} />

          {/* Certificate Number */}
          <div style={styles.certNumRow}>
            Certificate No. <span style={{ fontWeight: 'bold' }}>{data.certificate_no || ''}</span>
          </div>

          {/* Photos Row */}
          <table style={{ width: '100%', borderCollapse: 'collapse', margin: '3px 0' }}>
            <tbody>
              <tr>
                {/* Husband Photo */}
                <td style={{ width: '105px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={styles.photoContainer}>
                    <div style={styles.photoFrame}>
                      {husbandPhotoUrl ? (
                        <img src={husbandPhotoUrl} alt="Groom" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '8px', color: '#aaa', fontStyle: 'italic', textAlign: 'center' }}>वर<br/>Groom</span>
                      )}
                    </div>
                    {stampUrl && (
                      <div style={{ ...styles.stampOverlay, right: '-29px' }}>
                        <img src={stampUrl} alt="Stamp" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </div>
                    )}
                  </div>
                  <div style={styles.photoCaption}>वर / Groom</div>

                  <div style={{ textAlign: 'center', marginTop: '5px' }}>
                    <div style={styles.sigBox}>
                      {husbandSigUrl ? (
                        <img src={husbandSigUrl} alt="Signature" style={{ maxHeight: '22px', objectFit: 'contain' }} />
                      ) : (
                        <span style={{ fontSize: '7px', color: '#ccc', fontStyle: 'italic' }}>Signature</span>
                      )}
                    </div>
                    <div style={styles.sigCaption}>वर / Groom</div>
                  </div>
                </td>

                {/* Center */}
                <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '0 5px' }}>
                  <div style={{ fontSize: '9px', color: '#1a3a6b' }}>
                    {data.certificate_no && <span style={{ fontFamily: 'Georgia, serif', fontWeight: 600 }}>No. {data.certificate_no}</span>}
                  </div>
                </td>

                {/* Wife Photo */}
                <td style={{ width: '105px', textAlign: 'center', verticalAlign: 'top' }}>
                  <div style={styles.photoContainer}>
                    <div style={styles.photoFrame}>
                      {wifePhotoUrl ? (
                        <img src={wifePhotoUrl} alt="Bride" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '8px', color: '#aaa', fontStyle: 'italic', textAlign: 'center' }}>वधू<br/>Bride</span>
                      )}
                    </div>
                    {stampUrl && (
                      <div style={{ ...styles.stampOverlay, left: '-29px' }}>
                        <img src={stampUrl} alt="Stamp" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </div>
                    )}
                  </div>
                  <div style={styles.photoCaption}>वधू / Bride</div>

                  <div style={{ textAlign: 'center', marginTop: '5px' }}>
                    <div style={styles.sigBox}>
                      {wifeSigUrl ? (
                        <img src={wifeSigUrl} alt="Signature" style={{ maxHeight: '22px', objectFit: 'contain' }} />
                      ) : (
                        <span style={{ fontSize: '7px', color: '#ccc', fontStyle: 'italic' }}>Signature</span>
                      )}
                    </div>
                    <div style={styles.sigCaption}>वधू / Bride</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Certificate Body */}
          <div style={{ padding: '2px 0' }}>
            {/* Marathi Block */}
            {showMarathi && (
              <div style={styles.bodyBlock}>
                <div style={{ fontWeight: 700, marginBottom: '3px' }}>प्रमाणित करण्यात येते की,</div>
                <div style={{ marginBottom: '2px' }}>
                  पतीचे नाव : <span style={styles.underline}>{data.husband_name || ' '}</span>
                  {' '}वय : <span style={styles.underline}>{data.husband_age || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  राहणार : <span style={{ ...styles.underline, minWidth: '150px' }}>{data.husband_address || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  पत्नीचे नाव : <span style={styles.underline}>{data.wife_name || ' '}</span>
                  {' '}वय : <span style={styles.underline}>{data.wife_age || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  राहणार : <span style={{ ...styles.underline, minWidth: '150px' }}>{data.wife_address || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  विवाह दिनांक : <span style={styles.underline}>{marriageDate?.display || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  विवाह स्थळ : <span style={styles.underline}>{data.marriage_place || data.place || '-'}</span>
                </div>
                {data.volume_no && (
                  <div style={{ marginBottom: '2px' }}>
                    खंड क्र. : <span style={styles.underline}>{data.volume_no}</span>
                  </div>
                )}
                {data.serial_no && (
                  <div style={{ marginBottom: '2px' }}>
                    पृष्ठक्रमांक : <span style={styles.underline}>{data.serial_no}</span>
                  </div>
                )}
                {data.registration_date && (
                  <div style={{ marginBottom: '2px' }}>
                    नोंदणी दिनांक : <span style={styles.underline}>{formatDate(data.registration_date)?.display || '-'}</span>
                  </div>
                )}

                {showEnglish && <hr style={{ margin: '5px 0', border: 'none', borderTop: '1px solid #d4a843' }} />}
              </div>
            )}

            {/* English Block */}
            {showEnglish && (
              <div style={styles.bodyBlock}>
                <div style={{ fontWeight: 700, marginBottom: '3px' }}>This is to certify that,</div>
                <div style={{ marginBottom: '2px' }}>
                  Groom Name : <span style={styles.underline}>{data.husband_name || ' '}</span>
                  {' '}Age : <span style={styles.underline}>{data.husband_age || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  Address : <span style={{ ...styles.underline, minWidth: '150px' }}>{data.husband_address || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  Bride Name : <span style={styles.underline}>{data.wife_name || ' '}</span>
                  {' '}Age : <span style={styles.underline}>{data.wife_age || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  Address : <span style={{ ...styles.underline, minWidth: '150px' }}>{data.wife_address || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  Marriage Date : <span style={styles.underline}>{marriageDate?.display || '-'}</span>
                </div>
                <div style={{ marginBottom: '2px' }}>
                  Marriage Place : <span style={styles.underline}>{data.marriage_place || data.place || '-'}</span>
                </div>
                {data.volume_no && (
                  <div style={{ marginBottom: '2px' }}>
                    Volume No. : <span style={styles.underline}>{data.volume_no}</span>
                  </div>
                )}
                {data.serial_no && (
                  <div style={{ marginBottom: '2px' }}>
                    Serial No. : <span style={styles.underline}>{data.serial_no}</span>
                  </div>
                )}
                {data.registration_date && (
                  <div style={{ marginBottom: '2px' }}>
                    Registration Date : <span style={styles.underline}>{formatDate(data.registration_date)?.display || '-'}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom Section */}
          <table style={styles.bottomTable}>
            <tbody>
              <tr>
                {/* Place + Date + QR */}
                <td style={{ width: '30%', verticalAlign: 'top', padding: '0 4px' }}>
                  <div style={{ marginBottom: '5px' }}>
                    <div style={styles.detailLabel}>ठिकाण – Place</div>
                    <div style={styles.detailValue}>{data.place || '-'}</div>
                  </div>
                  <div style={{ marginBottom: '5px' }}>
                    <div style={styles.detailLabel}>दिनांक – Date</div>
                    <div style={styles.detailValue}>{certificateDate?.display || '-'}</div>
                  </div>
                  <div>
                    <div style={styles.qrBox}>
                      {qrUrl ? (
                        <img src={qrUrl} alt="QR Code" style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
                      ) : (
                        <span style={{ fontSize: '8px', color: '#aaa', fontStyle: 'italic', textAlign: 'center' }}>QR<br/>Code</span>
                      )}
                    </div>
                    <div style={{ fontSize: '7px', color: '#1a3a6b', marginTop: '1px' }}>स्कॅन करा / Scan to Verify</div>
                  </div>
                </td>

                {/* Signatures */}
                <td style={{ width: '30%', verticalAlign: 'top', padding: '0 4px' }}>
                  {/* Empty - signatures are under photos */}
                </td>

                {/* Authority */}
                <td style={{ width: '35%', verticalAlign: 'bottom', textAlign: 'center', padding: '0 4px' }}>
                  <div style={styles.stampBox}>
                    {stampUrl ? (
                      <img src={stampUrl} alt="Stamp" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <div style={{
                        width: '65px', height: '65px',
                        border: '2px dashed #9c7a2e', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '8px', color: '#bbb', fontStyle: 'italic', textAlign: 'center'
                      }}>
                        Official<br/>Stamp
                      </div>
                    )}
                  </div>
                  <div style={{
                    width: '75%', margin: '0 auto 2px', height: '24px',
                    borderBottom: '1px solid #9c7a2e',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {authoritySignatureUrl && (
                      <img src={authoritySignatureUrl} alt="Authority Signature" style={{ maxHeight: '22px', maxWidth: '100%', objectFit: 'contain' }} />
                    )}
                  </div>
                  <div style={{ fontSize: '9px', color: '#1a3a6b', fontWeight: 600 }}>सही / Signature</div>
                  <div style={{ fontSize: '8px', color: '#1a3a6b', fontStyle: 'italic', lineHeight: 1.3 }}>
                    {data.authority_name || ' '}
                    {officeName && <div style={{ fontSize: '8px' }}>{officeName}</div>}
                    {officeAddress && <div style={{ fontSize: '8px' }}>{officeAddress}</div>}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Footer Band */}
          <div style={styles.footerBand}>
            <div style={styles.footerText}>
              {officeName || 'Shree Sai Vaidik Vivah'}{officeName && officeAddress ? ', ' : ''}{officeAddress || '499, Gharpure Ghat, Ashok Stambh, Nashik-422001'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CertificatePreview;